(() => {
  const root = document.documentElement;
  root.classList.add('iuf-js');

  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveData = Boolean(navigator.connection && navigator.connection.saveData);
  const isMobile = () => matchMedia('(max-width: 749px)').matches;

  const init = () => {
    initReveal();
    initAmbientVideos();
    initFilmPlayers();
    initTransform();
    initSticky();
  };

  function initReveal() {
    const items = document.querySelectorAll('[data-reveal]');
    if (!('IntersectionObserver' in window) || reduceMotion) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );
    items.forEach((el) => io.observe(el));
  }

  // Vidéos d'ambiance muettes : chargées à l'approche, lues seulement quand elles sont visibles.
  function initAmbientVideos() {
    const videos = document.querySelectorAll('video[data-iuf-ambient]');
    if (!videos.length || reduceMotion || saveData) return;

    const io = new IntersectionObserver(
      (entries) => {
        for (const { target: video, isIntersecting } of entries) {
          if (isIntersecting) {
            if (!video.getAttribute('src')) {
              video.src = (isMobile() && video.dataset.srcMobile) || video.dataset.src;
            }
            video.play().catch(() => {});
          } else if (!video.paused) {
            video.pause();
          }
        }
      },
      { threshold: 0.15 }
    );

    videos.forEach((video) => {
      video.muted = true;
      video.addEventListener('playing', () => video.classList.add('is-playing'), { once: true });
      io.observe(video);
    });
  }

  // Lecteur du film d'exemple, avec ses chapitres.
  function initFilmPlayers() {
    document.querySelectorAll('[data-iuf-player]').forEach((frame) => {
      const video = frame.querySelector('video');
      const playButton = frame.querySelector('[data-iuf-play]');
      const chapters = [...document.querySelectorAll(`[data-iuf-chapter][data-for="${frame.id}"]`)];
      if (!video) return;

      const start = (time) => {
        if (!video.getAttribute('src')) video.src = video.dataset.src;
        frame.classList.add('is-started');
        video.controls = true;
        if (typeof time === 'number') {
          const seek = () => {
            video.currentTime = time;
          };
          if (video.readyState >= 1) seek();
          else video.addEventListener('loadedmetadata', seek, { once: true });
        }
        video.play().catch(() => {});
      };

      playButton?.addEventListener('click', () => start());

      chapters.forEach((chapter) => {
        chapter.addEventListener('click', () => {
          const rect = frame.getBoundingClientRect();
          if (rect.top < 0 || rect.bottom > innerHeight) {
            frame.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
          }
          start(parseFloat(chapter.dataset.t));
        });
      });

      video.addEventListener('timeupdate', () => {
        let current = -1;
        chapters.forEach((chapter, index) => {
          if (video.currentTime >= parseFloat(chapter.dataset.t) - 0.15) current = index;
        });
        chapters.forEach((chapter, index) => chapter.setAttribute('aria-current', String(index === current)));
      });

      document.querySelectorAll(`[data-iuf-play-film="${frame.id}"]`).forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          frame.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
          start();
        });
      });

      new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting && !video.paused) video.pause();
        },
        { threshold: 0 }
      ).observe(frame);
    });
  }

  // Photo → personnages → scène → film : l'image change selon l'étape lue.
  function initTransform() {
    document.querySelectorAll('[data-iuf-transform]').forEach((section) => {
      const steps = [...section.querySelectorAll('[data-step]')];
      const layers = [...section.querySelectorAll('[data-layer]')];
      const counter = section.querySelector('[data-iuf-counter]');
      const stageVideo = section.querySelector('video[data-iuf-stage-video]');
      let current = -1;

      const setStep = (index) => {
        if (index === current || index < 0) return;
        current = index;
        layers.forEach((layer, i) => {
          layer.classList.toggle('is-active', i === index);
          layer.classList.toggle('is-past', i < index);
        });
        steps.forEach((step, i) => step.classList.toggle('is-active', i === index));
        if (counter) counter.textContent = String(index + 1).padStart(2, '0');
        section.style.setProperty('--progress', String((index + 1) / steps.length));

        if (!stageVideo) return;
        const isVideoStep = layers[index]?.contains(stageVideo);
        if (isVideoStep && !reduceMotion && !saveData) {
          if (!stageVideo.getAttribute('src')) stageVideo.src = stageVideo.dataset.src;
          stageVideo.currentTime = 0;
          stageVideo.play().catch(() => {});
        } else {
          stageVideo.pause();
        }
      };

      const io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) setStep(steps.indexOf(entry.target));
          }
        },
        { rootMargin: '-42% 0px -42% 0px' }
      );
      steps.forEach((step) => io.observe(step));
      setStep(0);
    });
  }

  // Bouton « Créer notre film » toujours à portée, sauf quand un autre appel est déjà visible.
  function initSticky() {
    const bar = document.querySelector('[data-iuf-sticky]');
    if (!bar) return;
    const watched = [
      document.querySelector('[data-iuf-hero]'),
      ...document.querySelectorAll('[data-iuf-hide-sticky]'),
    ].filter(Boolean);
    const visible = new Map();

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => visible.set(entry.target, entry.isIntersecting));
        const show = watched.every((el) => !visible.get(el));
        bar.classList.toggle('is-visible', show);
        bar.toggleAttribute('inert', !show);
      },
      { threshold: 0 }
    );
    watched.forEach((el) => io.observe(el));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
